import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { fmtShort } from "@/lib/datetime";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Truck, MapPin, CheckCircle2, Map as MapIcon, Navigation, LocateFixed, Banknote, CreditCard, Wallet, Timer } from "lucide-react";
import BootMap, { parseCoords, distanceMiles } from "@/components/BootMap";
import EvidenceGallery from "@/components/EvidenceGallery";
import EvidenceSlots from "@/components/EvidenceSlots";
import MultiMediaUpload from "@/components/MultiMediaUpload";
import PmcPayLookup from "@/components/PmcPayLookup";
import TimeClockWidget from "@/components/TimeClockWidget";
import WhatsAppButton, { bootMessage } from "@/components/WhatsAppButton";

const REMOVE_SLOTS = [
  { key: "id_front", label: "ID — front", kind: "image" },
  { key: "id_back", label: "ID — back", kind: "image" },
  { key: "window_notice", label: "Signed window notice", kind: "image" },
];
const METHODS = [
  { value: "cash", label: "Cash", Icon: Banknote },
  { value: "card", label: "Card", Icon: CreditCard },
  { value: "other", label: "Other", Icon: Wallet },
];

function Countdown({ v, slaMin }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 15000); return () => clearInterval(t); }, []);
  if (!v.paid_at) return null;
  const left = Math.round(slaMin - (Date.now() - new Date(v.paid_at).getTime()) / 60000);
  const overdue = left < 0;
  const urgent = !overdue && left <= 15;
  return (
    <div className={`rounded-md px-3 py-2 flex items-center gap-2 text-sm font-bold border ${overdue ? "bg-red-500/15 border-red-500/50 text-red-300" : urgent ? "bg-amber-500/15 border-amber-500/50 text-amber-300" : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"}`}
      data-testid={`countdown-${v.plate_number}`}>
      <Timer className="w-4 h-4" />
      {overdue ? `OVERDUE by ${Math.abs(left)} min — remove now` : `${left} min left to remove (paid ${fmtShort(v.paid_at)})`}
    </div>
  );
}

export default function RemovalDashboard() {
  const [queue, setQueue] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [cfg, setCfg] = useState({ default_fee: 138, removal_sla_min: 59, incorrect_entry_fee: 28 });
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ method: "cash", fee: "138", receipts: [], category: "standard" });
  const [removeModal, setRemoveModal] = useState(null);
  const [removePhotos, setRemovePhotos] = useState({});
  const [removeExtra, setRemoveExtra] = useState([]);
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const [gpsState, setGpsState] = useState("idle");
  const feesToday = [...completed, ...queue.filter((v) => v.status === "paid")].reduce((s, v) => s + Number(v.fee_amount || 0), 0);

  const locate = () => {
    if (!navigator.geolocation) { setGpsState("unavailable"); return; }
    setGpsState("locating");
    navigator.geolocation.getCurrentPosition(
      (p) => { setMe([p.coords.latitude, p.coords.longitude]); setGpsState("locked"); },
      () => setGpsState("failed"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  useEffect(() => { locate(); api.get("/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const withDist = queue.map((v) => ({ v, dist: me && parseCoords(v) ? distanceMiles(me, parseCoords(v)) : null }));
  const sortedQueue = [...withDist].sort((a, b) => {
    const pa = a.v.status === "paid" ? 0 : 1, pb = b.v.status === "paid" ? 0 : 1;
    if (pa !== pb) return pa - pb;
    if (pa === 0) return new Date(a.v.paid_at) - new Date(b.v.paid_at);
    return me ? (a.dist ?? Infinity) - (b.dist ?? Infinity) : 0;
  });

  const load = async () => {
    const [q, c] = await Promise.all([
      api.get("/vehicles?scope=booted"),
      api.get(`/vehicles?scope=my_removals&shift_date=${new Date().toISOString().slice(0, 10)}`).catch(() => ({ data: [] })),
    ]);
    setQueue(q.data);
    setCompleted(c.data.filter((v) => v.status === "removed"));
  };
  usePolling(load, 8000);

  const openPay = (v) => { setPayModal(v); setPayForm({ method: "cash", fee: String(cfg.default_fee), receipts: [], category: "standard" }); };
  const submitPay = async () => {
    const amt = Number(payForm.fee);
    if (Number.isNaN(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
    if (!payForm.receipts?.length) { toast.error("At least one receipt photo is required"); return; }
    setBusy(true);
    try {
      await api.post(`/vehicles/${payModal.id}/pay`, { payment_method: payForm.method, fee_amount: amt, fee_category: payForm.category, receipt_photo_path: payForm.receipts[0], receipt_photo_paths: payForm.receipts });
      toast.success(`Payment recorded — ${cfg.removal_sla_min} min to remove ${payModal.plate_number}`);
      setPayModal(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Payment failed"); }
    finally { setBusy(false); }
  };

  const openRemove = (v) => { setRemoveModal(v); setRemovePhotos({}); setRemoveExtra([]); };
  const submitRemove = async () => {
    const missing = REMOVE_SLOTS.filter((s) => !removePhotos[s.key]);
    if (missing.length) { toast.error(`Missing: ${missing.map((s) => s.label).join(", ")}`); return; }
    setBusy(true);
    try {
      await api.post(`/vehicles/${removeModal.id}/remove`, { removal_photos: removePhotos, removal_extra_paths: removeExtra });
      toast.success(`${removeModal.plate_number} removed — enforcer notified`);
      setRemoveModal(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  const inputCls = "bg-[#0B0F17] border-[#28364F] text-white";

  return (
    <AppShell>
      <div className="mb-5">
        <h1 className="font-display text-3xl font-black tracking-tight">Removal Dispatch</h1>
        <p className="text-sm text-[#94A3B8]">Booted vehicles awaiting payment & pickup — {cfg.removal_sla_min}-min removal window after payment</p>
      </div>
      <div className="mb-4"><TimeClockWidget /></div>
      <div className="grid grid-cols-3 gap-2 mb-5">
        <Card className="bg-[#131B2A] border-[#28364F] p-3">
          <div className="text-[10px] uppercase tracking-widest text-red-300">In queue</div>
          <div className="text-3xl font-black font-display" data-testid="stat-queue">{queue.length}</div>
        </Card>
        <Card className="bg-[#131B2A] border-[#28364F] p-3">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">Removed today</div>
          <div className="text-3xl font-black font-display" data-testid="stat-completed">{completed.length}</div>
        </Card>
        <Card className="bg-[#131B2A] border-[#28364F] p-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-300">Fees today</div>
          <div className="text-2xl font-black font-display" data-testid="stat-fees">${feesToday.toFixed(2)}</div>
        </Card>
      </div>

      <div className="mb-6"><PmcPayLookup /></div>
      <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><MapIcon className="w-5 h-5 text-cyan-400" /> Live Boot Map</h2>
      <div className="mb-6"><BootMap vehicles={queue} /></div>

      <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2 flex-wrap">
        <Truck className="w-5 h-5 text-red-400" /> Active Queue
        <span className="text-xs font-normal text-[#94A3B8] ml-auto inline-flex items-center gap-1" data-testid="queue-sort-status">
          {gpsState === "locked" && <><LocateFixed className="w-3.5 h-3.5 text-emerald-400" /> Paid first, then nearest</>}
          {gpsState === "locating" && "Locating you…"}
          {(gpsState === "failed" || gpsState === "unavailable") && (
            <button type="button" onClick={locate} className="text-amber-400 underline" data-testid="queue-locate-retry">Enable location to sort by distance</button>
          )}
        </span>
      </h2>
      {queue.length === 0 ? (
        <Card className="bg-[#131B2A] border-[#28364F] p-8 text-center text-[#94A3B8]">Queue is clear — no booted vehicles awaiting removal.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sortedQueue.map(({ v, dist }, idx) => (
            <Card key={v.id} className={`bg-[#131B2A] p-4 space-y-3 ${v.status === "paid" ? "border-emerald-500/40" : "border-[#28364F] pulse-red"}`} data-testid={`queue-${v.plate_number}`}>
              <div className="flex items-start justify-between gap-2">
                <span className="plate-badge">{v.plate_number}</span>
                <div className="flex items-center gap-1.5">
                  {dist != null && (
                    <Badge className={`text-[10px] font-mono ${idx === 0 ? "bg-emerald-500 text-[#0B0F17]" : "bg-[#0B0F17] text-cyan-300 border border-[#28364F]"}`} data-testid={`dist-${v.plate_number}`}>
                      {dist < 0.1 ? `${Math.round(dist * 5280)} ft` : `${dist.toFixed(1)} mi`}
                    </Badge>
                  )}
                  <Badge className={`${v.status === "paid" ? "status-paid" : "status-boot"} uppercase tracking-widest text-[10px]`} data-testid={`status-${v.plate_number}`}>{v.status}</Badge>
                  {v.pmc_paid_flag && <Badge className="bg-red-500/20 text-red-300 border border-red-500/40 uppercase tracking-widest text-[10px]" data-testid={`pmc-flag-${v.plate_number}`}>PMC Paid</Badge>}
                </div>
              </div>
              {v.status === "paid" && <Countdown v={v} slaMin={cfg.removal_sla_min} />}
              <div className="text-sm text-[#F8FAFC] flex items-start gap-1.5"><MapPin className="w-4 h-4 mt-0.5 text-cyan-400" /><span className="font-medium">{v.location}</span></div>
              <div className="text-xs text-[#94A3B8]">
                Booted by <span className="text-white">{v.enforcer_name}</span> at <span className="font-mono">{fmtShort(v.booted_at)}</span>
                {v.status === "paid" && <> · <span className="text-amber-300 font-mono">${Number(v.fee_amount).toFixed(2)} {v.payment_method}</span></>}
              </div>
              <EvidenceGallery v={{ ...v, removal_photos: {}, receipt_photo_path: null }} className="h-24" testId={`queue-media-${v.plate_number}`} />
              <WhatsAppButton message={bootMessage(v)} label="Send plate to removal team" className="w-full" testId={`whatsapp-btn-${v.plate_number}`} />
              <div className="flex gap-2">
                <Button asChild variant="outline" className="shrink-0 bg-[#0B0F17] border-cyan-500/40 text-cyan-300 hover:bg-[#1C263B] hover:text-cyan-200 font-bold">
                  <a href={parseCoords(v) ? `https://www.google.com/maps/dir/?api=1&destination=${parseCoords(v).join(",")}&travelmode=driving` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.location)}`}
                    target="_blank" rel="noreferrer" data-testid={`drive-btn-${v.plate_number}`}>
                    <Navigation className="w-4 h-4 mr-1.5" /> {parseCoords(v) ? "Drive here" : "Search"}
                  </a>
                </Button>
                {v.status === "booted" ? (
                  <Button className="flex-1 bg-amber-500 hover:bg-amber-400 text-[#0B0F17] font-bold" onClick={() => openPay(v)} data-testid={`pay-btn-${v.plate_number}`}>
                    <Banknote className="w-4 h-4 mr-1.5" /> Record Payment
                  </Button>
                ) : (
                  <Button className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-bold" onClick={() => openRemove(v)} data-testid={`remove-btn-${v.plate_number}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Removed
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {completed.length > 0 && (
        <>
          <h2 className="font-display text-xl font-bold mt-8 mb-3">My Removals Today</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {completed.map((v) => (
              <Card key={v.id} className="bg-[#131B2A] border-[#28364F] p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="plate-badge">{v.plate_number}</span>
                  <span className="text-xs font-mono text-emerald-400">{fmtShort(v.removed_at)}</span>
                </div>
                <div className="text-xs text-[#94A3B8] flex justify-between">
                  <span>{v.location}</span>
                  <span className="text-amber-300 font-mono" data-testid={`fee-${v.plate_number}`}>${Number(v.fee_amount || 0).toFixed(2)} {v.payment_method || ""}</span>
                </div>
                <EvidenceGallery v={{ ...v, tag_photo_paths: [], tag_photo_path: null, boot_photo_paths: [], boot_photo_path: null, boot_video_path: null }} cols="grid-cols-4" className="h-20" testId={`done-media-${v.plate_number}`} />
              </Card>
            ))}
          </div>
        </>
      )}

      {payModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="pay-modal">
          <Card className="bg-[#131B2A] border-[#28364F] p-5 max-w-md w-full space-y-4 max-h-[92vh] overflow-y-auto">
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-300">Record Payment</div>
              <div className="mt-1"><span className="plate-badge">{payModal.plate_number}</span></div>
              <div className="text-sm text-[#94A3B8] mt-2">{payModal.location}</div>
            </div>
            <div>
              <Label>Fee type</Label>
              <div className="grid grid-cols-1 gap-2 mt-1">
                <button type="button" onClick={() => setPayForm({ ...payForm, category: "standard", fee: String(cfg.default_fee) })}
                  className={`text-left rounded-md border px-3 py-2 text-sm transition-colors ${payForm.category === "standard" ? "bg-amber-500/15 border-amber-500 text-amber-100" : "bg-[#0B0F17] border-[#28364F] text-[#94A3B8] hover:border-amber-500/50"}`}
                  data-testid="pay-category-standard">
                  <span className="font-bold">Standard removal</span> — booter earns ${Number(cfg.booter_pay_standard ?? 20).toFixed(2)}
                </button>
                <button type="button" onClick={() => setPayForm({ ...payForm, category: "incorrect_entry", fee: String(cfg.incorrect_entry_fee ?? 28) })}
                  className={`text-left rounded-md border px-3 py-2 text-sm transition-colors ${payForm.category === "incorrect_entry" ? "bg-cyan-500/15 border-cyan-500 text-cyan-100" : "bg-[#0B0F17] border-[#28364F] text-[#94A3B8] hover:border-cyan-500/50"}`}
                  data-testid="pay-category-incorrect">
                  <span className="font-bold">Paid to park, wrong entry (${Number(cfg.incorrect_entry_fee ?? 28).toFixed(0)})</span> — phone# not plate; booter earns ${Number(cfg.booter_pay_incorrect ?? 12.5).toFixed(2)}
                </button>
              </div>
            </div>
            <div>
              <Label>Paid by</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {METHODS.map(({ value, label, Icon }) => (
                  <button key={value} type="button" onClick={() => setPayForm({ ...payForm, method: value })}
                    className={`h-12 rounded-md border font-bold text-sm flex items-center justify-center gap-1.5 transition-colors ${payForm.method === value ? "bg-amber-500 text-[#0B0F17] border-amber-500" : "bg-[#0B0F17] text-[#94A3B8] border-[#28364F] hover:border-amber-500/50"}`}
                    data-testid={`pay-method-${value}`} aria-pressed={payForm.method === value}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Amount ($) — standard removal fee ${Number(cfg.default_fee).toFixed(2)}</Label>
              <Input type="number" inputMode="decimal" min="0" step="0.01" value={payForm.fee}
                onChange={(e) => setPayForm({ ...payForm, fee: e.target.value })}
                className={`${inputCls} text-lg font-mono`} data-testid="pay-fee-input" />
            </div>
            <div>
              <Label>Paid receipt photo(s) — at least 1, add more if needed</Label>
              <MultiMediaUpload value={payForm.receipts} onChange={(r) => setPayForm({ ...payForm, receipts: r })} min={1} testId="pay-receipt" />
            </div>
            <p className="text-xs text-[#94A3B8]">After saving, you have <b className="text-white">{cfg.removal_sla_min} minutes</b> to remove the boot. You'll get a reminder at 30 min.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 bg-transparent border-[#28364F] text-white hover:bg-[#1C263B]" onClick={() => setPayModal(null)} data-testid="pay-cancel-btn">Cancel</Button>
              <Button className="flex-1 bg-amber-500 hover:bg-amber-400 text-[#0B0F17] font-bold" onClick={submitPay} disabled={busy} data-testid="pay-confirm-btn">
                {busy ? "Saving..." : "Save Payment & Start Timer"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {removeModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="remove-modal">
          <Card className="bg-[#131B2A] border-[#28364F] p-5 max-w-md w-full space-y-4 max-h-[92vh] overflow-y-auto">
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-300">Confirm Removal</div>
              <div className="mt-1"><span className="plate-badge">{removeModal.plate_number}</span></div>
              <div className="text-sm text-[#94A3B8] mt-2">{removeModal.location} · paid ${Number(removeModal.fee_amount).toFixed(2)} {removeModal.payment_method}</div>
            </div>
            <Countdown v={removeModal} slaMin={cfg.removal_sla_min} />
            <div>
              <Label>Removal evidence (all 3 required)</Label>
              <EvidenceSlots slots={REMOVE_SLOTS} values={removePhotos} onChange={setRemovePhotos} testId="receipt-photo" />
            </div>
            <div>
              <Label>Extra photos / videos (optional — pick several from gallery)</Label>
              <MultiMediaUpload value={removeExtra} onChange={setRemoveExtra} testId="remove-extra" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 bg-transparent border-[#28364F] text-white hover:bg-[#1C263B]" onClick={() => setRemoveModal(null)} data-testid="remove-cancel-btn">Cancel</Button>
              <Button className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-bold" onClick={submitRemove} disabled={busy} data-testid="remove-confirm-btn">
                {busy ? "Saving..." : "Confirm & Notify"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
